/**
 * One burst of confetti, for the one screen that has earned it.
 *
 * A canvas rather than a few hundred DOM nodes: the particles are the only thing
 * moving, none of them is interactive, and animating transforms on that many
 * elements makes the page stutter on the laptop this product's customer actually
 * uses.
 *
 * Three rules keep it from becoming the thing people remember about the product:
 *
 * - **It ends.** The animation runs once, stops itself, and the canvas comes out
 *   of the tree. There is no loop and nothing left running behind the card.
 * - **It cannot be clicked.** `pointer-events: none` and `aria-hidden`, so it is
 *   invisible to the keyboard, to a screen reader, and to the mouse. A customer
 *   heading for "Back to the evidence bank" never hits it.
 * - **It respects `prefers-reduced-motion`.** Somebody who has asked the whole
 *   operating system to stop moving things has asked us too, and a celebration
 *   is exactly the kind of animation that setting exists for. They get the card
 *   and nothing else.
 *
 * The colours are read from the live theme rather than hard-coded, so the burst
 * belongs to the page it lands on in both light and dark.
 */

import { useEffect, useRef } from 'react';

const DURATION_MS = 2400;
const PARTICLE_COUNT = 90;
const GRAVITY = 0.16;
const DRAG = 0.992;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
  /** Squashes the ribbon as it turns, so a flat rectangle reads as tumbling. */
  wobble: number;
}

function paletteFrom(root: HTMLElement): string[] {
  const styles = getComputedStyle(root);
  const accent = styles.getPropertyValue('--vk-accent').trim() || '#0f766e';
  const hover = styles.getPropertyValue('--vk-accent-hover').trim() || accent;
  const ink = styles.getPropertyValue('--vk-ink').trim() || '#111111';
  // Two neutrals against two accents. More colours than this stops reading as
  // one product's celebration and starts reading as a birthday card.
  return [accent, accent, hover, ink, '#f59e0b'];
}

export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const palette = paletteFrom(document.documentElement);

    // Two launch points rather than one, angled inwards. A single central
    // fountain looks like a bug in a particle system; two crossing arcs look
    // deliberate, which is the whole difference.
    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const fromLeft = i % 2 === 0;
      const spread = (Math.random() - 0.5) * 1.1;
      return {
        x: fromLeft ? width * 0.18 : width * 0.82,
        y: height * 0.42,
        vx: (fromLeft ? 1 : -1) * (2.2 + Math.random() * 3.4) + spread,
        vy: -(5.5 + Math.random() * 4.5),
        size: 5 + Math.random() * 5,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.28,
        color: palette[Math.floor(Math.random() * palette.length)],
        wobble: Math.random() * Math.PI * 2,
      };
    });

    const startedAt = performance.now();
    let frame = 0;

    const draw = (now: number) => {
      const elapsed = now - startedAt;
      if (elapsed >= DURATION_MS) {
        context.clearRect(0, 0, width, height);
        return;
      }

      // Fade over the last third rather than cutting to nothing, which would
      // read as the tab dropping frames.
      const fade = elapsed < DURATION_MS * 0.66 ? 1 : 1 - (elapsed - DURATION_MS * 0.66) / (DURATION_MS * 0.34);

      context.clearRect(0, 0, width, height);
      context.globalAlpha = Math.max(fade, 0);

      for (const p of particles) {
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;
        p.wobble += 0.1;

        context.save();
        context.translate(p.x, p.y);
        context.rotate(p.rotation);
        context.fillStyle = p.color;
        const squash = Math.abs(Math.cos(p.wobble));
        context.fillRect(-p.size / 2, (-p.size * squash) / 2, p.size, p.size * squash);
        context.restore();
      }

      context.globalAlpha = 1;
      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-50 h-full w-full"
    />
  );
}
